import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertCircle, Search, Building } from 'lucide-react';
import { toast } from 'sonner';
import AppHeader from '../components/AppHeader';
import M45NavPills from '../components/M45NavPills';

const API = process.env.REACT_APP_BACKEND_URL;

const QuoteCreate = ({ user }) => {
  const navigate = useNavigate();
  const [customerName, setCustomerName] = useState('');
  const [quoteCustomers, setQuoteCustomers] = useState([]);
  const [customerDatabase, setCustomerDatabase] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSimilarModal, setShowSimilarModal] = useState(false);
  const [similarCustomer, setSimilarCustomer] = useState(null);
  const [selectedDbCustomer, setSelectedDbCustomer] = useState(null);

  const loadQuoteCustomers = async () => {
    try {
      const response = await api.get(`${API}/api/quotes/customers`);
      setQuoteCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Error loading quote customers:', error);
    }
  };

  useEffect(() => {
    loadQuoteCustomers();
  }, []);
  
  const loadCustomerDatabase = async (searchQuery = '') => {
    if (searchQuery.length < 2) {
      setCustomerDatabase([]);
      return;
    }
    try {
      const response = await api.get(`${API}/api/customers/search?q=${encodeURIComponent(searchQuery)}&limit=10`);
      setCustomerDatabase(response.data.results || []);
    } catch (error) {
      console.error('Error loading customer database:', error);
    }
  };

  const findSimilarQuoteCustomer = (name) => {
    if (!name || name.length < 3) return null;
    
    const nameLower = name.toLowerCase().trim();
    
    // Find exact or very similar match in quote customers
    const similar = quoteCustomers.find(c => {
      const customerNameLower = c.name.toLowerCase().trim();
      return customerNameLower === nameLower || 
             customerNameLower.includes(nameLower) ||
             nameLower.includes(customerNameLower);
    });
    
    return similar;
  };

  const handleCustomerNameChange = async (value) => {
    setCustomerName(value);
    setSelectedDbCustomer(null);
    
    // Search customer database as user types
    if (value.length >= 2) {
      await loadCustomerDatabase(value);
    } else {
      setSuggestions([]);
      setCustomerDatabase([]);
    }
  };
  
  // Update suggestions when customerDatabase changes
  useEffect(() => {
    if (customerDatabase.length > 0) {
      setSuggestions(customerDatabase.slice(0, 8));
    }
  }, [customerDatabase]);

  const handleSelectCustomer = (customer) => {
    const name = customer.ledger_name || customer.account_name || customer.name;
    setCustomerName(name);
    // Store full customer object with address details for auto-fill
    setSelectedDbCustomer({
      ...customer,
      // Build address from available fields
      customer_address: buildCustomerAddress(customer),
      delivery_address: buildDeliveryAddress(customer)
    });
    setSuggestions([]);
  };

  // Helper to build customer address from available fields
  const buildCustomerAddress = (customer) => {
    const parts = [];
    if (customer.billing_street) parts.push(customer.billing_street);
    if (customer.billing_city) parts.push(customer.billing_city);
    if (customer.billing_code) parts.push(customer.billing_code);
    if (customer.billing_country) parts.push(customer.billing_country);
    return parts.filter(Boolean).join('\n');
  };

  // Helper to build delivery address from available fields
  // Falls back to customer/billing address if no delivery address is set
  const buildDeliveryAddress = (customer) => {
    // First try delivery address fields
    const deliveryParts = [];
    if (customer.delivery_street) deliveryParts.push(customer.delivery_street);
    if (customer.delivery_city) deliveryParts.push(customer.delivery_city);
    if (customer.delivery_code) deliveryParts.push(customer.delivery_code);
    if (customer.delivery_country) deliveryParts.push(customer.delivery_country);
    
    // If delivery address exists, use it
    if (deliveryParts.filter(Boolean).length > 0) {
      return deliveryParts.filter(Boolean).join('\n');
    }
    
    // Otherwise, fall back to billing/customer address
    return buildCustomerAddress(customer);
  };

  const handleCustomerNameSubmit = () => {
    if (!customerName.trim()) {
      toast.error('Please enter or select a customer name');
      return;
    }

    // Check for similar customer in existing quotes
    const similar = findSimilarQuoteCustomer(customerName);
    
    if (similar) {
      setSimilarCustomer(similar);
      setShowSimilarModal(true);
    } else {
      // No similar customer, proceed with creating new quote
      createBlankQuote(customerName);
    }
  };

  const handleSimilarCustomerConfirm = (useExisting) => {
    if (useExisting) {
      // Use the existing customer name
      createBlankQuote(similarCustomer.name);
    } else {
      // User wants to use a different name, close modal and let them edit
      setShowSimilarModal(false);
      toast.info('Please enter the correct customer name');
    }
  };

  const createBlankQuote = async (finalCustomerName) => {
    try {
      // Pass selected customer details if available, including auto-filled addresses
      const payload = {
        customer_name: finalCustomerName,
        customer_db_id: selectedDbCustomer?.id || null,  // Link to main customer database
        customer_address: selectedDbCustomer?.customer_address || '',
        delivery_address: selectedDbCustomer?.delivery_address || ''
      };
      
      const response = await api.post(`${API}/api/quotes/create-blank`, payload);
      
      toast.success('Creating new quote...');
      window.dispatchEvent(new Event('c30:quotes-changed'));
      // Navigate to the edit page
      navigate(`/quotes/edit/${response.data.quote_id}`);
    } catch (error) {
      console.error('Error creating blank quote:', error);
      toast.error(error.response?.data?.detail || 'Failed to create quote');
    }
  };

  const enconOrange = '#ee7203';

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#e5e7eb' }}>
      {/* Global Header with standard navigation */}
      <AppHeader
        user={user}
        subHeaderLeft={
          <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
            <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Create Quote</span>
          </div>
        }
      />

      {/* Main Content */}
      <div className="pt-[20px] px-4 md:px-6 pb-6 bg-gray-100 min-h-screen">
        <div className="max-w-7xl mx-auto">
          
          {/* Quote Creation Card */}
          <div className="flex items-center justify-center mt-8">
            <Card className="w-full max-w-2xl bg-white">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Create New Quote</h1>
                  <p className="text-gray-600">Search and select a customer from the database</p>
                </div>

          <div className="space-y-6">
            <div>
              <Label className="text-lg">Customer Name *</Label>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    value={customerName}
                    onChange={(e) => handleCustomerNameChange(e.target.value)}
                    placeholder="Search customer database..."
                    className="text-lg py-6 mt-2 pl-10"
                    autoFocus
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleCustomerNameSubmit();
                      }
                    }}
                    data-testid="quote-customer-search"
                  />
                </div>
                
                {/* Suggestions Dropdown from Customer Database - Dark Style */}
                {suggestions.length > 0 && (
                  <div className="absolute z-[999] w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-[350px] overflow-y-auto">
                    {suggestions.map((customer, idx) => (
                      <div
                        key={idx}
                        className="p-3 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0"
                        onClick={() => handleSelectCustomer(customer)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <Building className="h-5 w-5 text-orange-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm text-white">{customer.ledger_name || customer.account_name}</p>
                            {/* Show branch/account name if different from ledger name */}
                            {customer.account_name && customer.account_name !== customer.ledger_name && (
                              <p className="text-xs text-orange-400">{customer.account_name}</p>
                            )}
                            {/* Show city/address */}
                            {(customer.billing_city || customer.billing_street) && (
                              <p className="text-xs text-gray-400 truncate">
                                {customer.billing_street ? `${customer.billing_street}, ` : ''}{customer.billing_city}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            {customer.billing_code && (
                              <p className="text-xs text-gray-300">{customer.billing_code}</p>
                            )}
                            {customer.customer_code && (
                              <p className="text-xs text-green-400">{customer.customer_code}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Selected Customer Details */}
              {selectedDbCustomer && (
                <div className="mt-4 p-4 bg-orange-50 border-2 border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-700 font-semibold mb-2">Selected Customer:</p>
                  <p className="font-bold text-gray-900">{selectedDbCustomer.ledger_name || selectedDbCustomer.name}</p>
                  {selectedDbCustomer.address_line_1 && (
                    <p className="text-sm text-gray-600">{selectedDbCustomer.address_line_1}</p>
                  )}
                  {selectedDbCustomer.postcode && (
                    <p className="text-sm text-gray-600">{selectedDbCustomer.postcode}</p>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                onClick={() => navigate('/quotes')}
                variant="outline"
                className="flex-1 py-6 text-lg rounded-full border-2 border-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCustomerNameSubmit}
                disabled={!customerName.trim()}
                className="flex-1 py-6 text-lg rounded-full bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="create-quote-submit"
              >
                Continue
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
          </div>
        </div>
      </div>

      {/* Similar Customer Modal */}
      <Dialog open={showSimilarModal} onOpenChange={setShowSimilarModal}>
        <DialogContent className="bg-white max-w-md overflow-hidden" style={{ borderRadius: '24px' }}>
          <DialogHeader>
            <DialogTitle className="text-xl">Similar Customer Found</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-800">
                A customer with a similar name already has quotes. Would you like to add to their existing folder?
              </p>
            </div>
            
            <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-500">You entered:</p>
              <p className="font-semibold">{customerName}</p>
            </div>
            
            <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
              <p className="text-sm text-gray-500">Existing customer:</p>
              <p className="font-semibold">{similarCustomer?.name}</p>
              <p className="text-xs text-gray-500">{similarCustomer?.quote_count || 0} existing quotes</p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => handleSimilarCustomerConfirm(false)}
                variant="outline"
                className="flex-1 py-6 text-lg rounded-full border-2"
              >
                Use Different Name
              </Button>
              <Button
                onClick={() => handleSimilarCustomerConfirm(true)}
                className="flex-1 py-6 text-lg rounded-full bg-orange-600 hover:bg-orange-700"
              >
                Use Existing
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuoteCreate;
